import Service, { inject as service } from '@ember/service';
import DS from 'ember-data';
import EnvironmentService from 'cio/services/environment';
import ObjectTypeModel from 'cio/models/object-type';
import AuthService from 'cio/services/auth';
import ApiService from 'cio/services/api';

export default class ObjectTypesService extends Service {
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service auth!: AuthService;
  @service api!: ApiService;

  get objectTypes() {
    let objectTypes = this.store.peekAll('object-type').toArray();
    if (objectTypes.length === 0) {
      // update in background
      this.store.findAll('object-type');
    }
    return objectTypes;
  }

  get sortedObjectTypes() {
    return this.#sortObjectTypes(this.objectTypes);
  }

  get enabledObjectTypes() {
    return this.objectTypes.filter(({ enabled }) => enabled);
  }

  get sortedEnabledObjectTypes() {
    return this.#sortObjectTypes(this.enabledObjectTypes);
  }

  get enabledObjectTypesCount() {
    return this.enabledObjectTypes.length;
  }

  // should be slug, but might as well be "id" with custom hand written URL
  getBySlug(slugOrId: string) {
    return (
      this.objectTypes.find((objectType) => objectType.slug === slugOrId) ??
      this.objectTypes.find((objectType) => objectType.id === slugOrId) // use this only as a fallback lookup
    );
  }

  get hasObjectTypes() {
    return this.objectTypes.length > 0;
  }

  get objectCount() {
    return this.objectTypes.reduce((acc, objectType) => acc + objectType.objectCount, 0);
  }

  get canConfigureObjects() {
    return (
      this.auth.hasPermission('journeys/objects/create') ||
      this.auth.hasPermission('journeys/objects/edit') ||
      this.auth.hasPermission('journeys/objects/delete')
    );
  }

  get canConfigureObjectTypes() {
    return (
      this.auth.hasPermission('journeys/object_types/create') ||
      this.auth.hasPermission('journeys/object_types/edit') ||
      this.auth.hasPermission('journeys/object_types/delete')
    );
  }

  get canCreateRelationships() {
    return this.auth.hasPermission('journeys/object_relationships/create');
  }

  exports({
    typeId,
    filters,
    attributes,
    from,
    includeRelationships,
  }: {
    typeId: string;
    filters: string | null;
    attributes: string[] | null;
    from: string;
    includeRelationships: boolean;
  }) {
    return this.api.post(`object_types/${typeId}/objects/exports`, {
      data: { filters, attributes, exported_from: from, export_relationships: includeRelationships },
    });
  }

  #sortObjectTypes(types: ObjectTypeModel[]) {
    return types.sort((a: ObjectTypeModel, b: ObjectTypeModel) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'object-types': ObjectTypesService;
  }
}
